import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, User as UserIcon, Box, CornerDownLeft } from 'lucide-react';
import { useLockerDrawer } from '../contexts/LockerDrawerContext';

/**
 * Cmd+K / Ctrl+K global search over guests (name, email, phone, RFID,
 * gym ID) and lockers (name, location). Selecting a guest deep-links to
 * their card on the Users page; selecting a locker opens the detail drawer.
 */
const CommandPalette = () => {
  const navigate = useNavigate();
  const { openLocker } = useLockerDrawer();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [lockers, setLockers] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);

  // Global shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Fresh data each time it opens
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlight(0);
    setTimeout(() => inputRef.current?.focus(), 30);
    Promise.all([axios.get('/api/users'), axios.get('/api/lockers')])
      .then(([u, l]) => {
        setUsers(u.data);
        setLockers(l.data);
      })
      .catch(() => {});
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const userHits = users
      .filter(u =>
        `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q) ||
        u.gym_id?.toLowerCase().includes(q) ||
        u.rfid_tag?.toLowerCase().includes(q)
      )
      .slice(0, 5)
      .map(u => ({ type: 'user', id: u.id, user: u }));
    const lockerHits = lockers
      .filter(l =>
        l.name?.toLowerCase().includes(q) ||
        l.location?.toLowerCase().includes(q)
      )
      .slice(0, 5)
      .map(l => ({ type: 'locker', id: l.id, locker: l }));
    return [...userHits, ...lockerHits];
  }, [query, users, lockers]);

  useEffect(() => setHighlight(0), [query]);

  const select = useCallback((item) => {
    setOpen(false);
    if (!item) return;
    if (item.type === 'user') {
      navigate(`/users?userId=${item.id}`);
    } else {
      openLocker(item.id);
    }
  }, [navigate, openLocker]);

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(results[highlight]);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-sm flex items-start justify-center pt-[14vh] px-4"
      onClick={() => setOpen(false)}
    >
      <div
        className="glass-sheet w-full max-w-xl overflow-hidden animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center px-5 py-4 border-b border-white/[0.08]">
          <Search className="w-5 h-5 text-secondary mr-3 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Search guests, RFID tags, lockers..."
            className="flex-1 bg-transparent text-body text-white placeholder:text-muted outline-none"
          />
          <kbd className="text-caption text-muted border border-white/15 rounded-md px-1.5 py-0.5 ml-3">esc</kbd>
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {query && results.length === 0 && (
            <p className="text-subheadline text-secondary text-center py-10">
              Nothing matches "{query}"
            </p>
          )}
          {!query && (
            <p className="text-footnote text-muted text-center py-8">
              Type a guest name, phone, RFID tag or locker number
            </p>
          )}
          {results.map((item, i) => (
            <button
              key={`${item.type}-${item.id}`}
              className={`w-full flex items-center px-5 py-3 text-left transition-colors ${
                i === highlight ? 'bg-primary-500/15' : 'hover:bg-white/[0.05]'
              }`}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => select(item)}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
                item.type === 'user' ? 'bg-primary-500/20 text-primary-300' : 'bg-success-500/20 text-success-400'
              }`}>
                {item.type === 'user' ? <UserIcon className="w-4 h-4" /> : <Box className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                {item.type === 'user' ? (
                  <>
                    <p className="text-subheadline text-white truncate">
                      {item.user.first_name} {item.user.last_name}
                    </p>
                    <p className="text-caption text-secondary truncate">
                      {[item.user.email, item.user.rfid_tag && `RFID ${item.user.rfid_tag}`].filter(Boolean).join('  ·  ')}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-subheadline text-white truncate">Locker {item.locker.name}</p>
                    <p className="text-caption text-secondary truncate capitalize">
                      {item.locker.status}{item.locker.location ? `  ·  ${item.locker.location}` : ''}
                    </p>
                  </>
                )}
              </div>
              {i === highlight && <CornerDownLeft className="w-4 h-4 text-muted ml-3 flex-shrink-0" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
